/**
 * Guest Pattern Storage Utilities
 * Manages localStorage for unauthenticated users creating patterns
 */

import { PatternDraft } from '@/types'

const GUEST_PATTERN_KEY = 'meshdom_guest_pattern'
const GUEST_PATTERN_EXPIRY_DAYS = 7

export interface GuestPatternData {
  draftData: PatternDraft
  timestamp: number
  tempId: string
  lastModified: number
}

/**
 * Save guest pattern data to localStorage
 */
export const saveGuestPattern = (draftData: PatternDraft): boolean => {
  try {
    const guestData: GuestPatternData = {
      draftData,
      timestamp: Date.now(),
      tempId: `guest_${Date.now()}`,
      lastModified: Date.now(),
    }

    const serialized = JSON.stringify(guestData)

    // Check size (localStorage limit is typically 5-10MB)
    const sizeInMB = new Blob([serialized]).size / (1024 * 1024)
    if (sizeInMB > 5) {
      console.warn('Guest pattern data exceeds 5MB, may fail to save')
    }

    localStorage.setItem(GUEST_PATTERN_KEY, serialized)
    return true
  } catch (error) {
    console.error('Failed to save guest pattern:', error)

    // If quota exceeded, try to clear and save again
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      console.warn('localStorage quota exceeded')
      return false
    }

    return false
  }
}

/**
 * Load guest pattern data from localStorage
 * Returns null if no data, expired, or corrupted
 */
export const loadGuestPattern = (): GuestPatternData | null => {
  try {
    const stored = localStorage.getItem(GUEST_PATTERN_KEY)
    if (!stored) return null

    const parsed: GuestPatternData = JSON.parse(stored)

    // Check if expired (7 days default)
    const expiryMs = GUEST_PATTERN_EXPIRY_DAYS * 24 * 60 * 60 * 1000
    const age = Date.now() - parsed.timestamp

    if (age > expiryMs) {
      console.log('Guest pattern expired, removing...')
      clearGuestPattern()
      return null
    }

    return parsed
  } catch (error) {
    console.error('Failed to load guest pattern:', error)
    // If corrupted, clear it
    clearGuestPattern()
    return null
  }
}

/**
 * Clear guest pattern data from localStorage
 */
export const clearGuestPattern = (): void => {
  try {
    localStorage.removeItem(GUEST_PATTERN_KEY)
  } catch (error) {
    console.error('Failed to clear guest pattern:', error)
  }
}

/**
 * Check if there's a saved guest pattern
 */
export const hasGuestPattern = (): boolean => {
  return loadGuestPattern() !== null
}

/**
 * Update guest pattern timestamp (for auto-save)
 */
export const updateGuestPatternTimestamp = (): boolean => {
  try {
    const existing = loadGuestPattern()
    if (!existing) return false

    existing.lastModified = Date.now()
    localStorage.setItem(GUEST_PATTERN_KEY, JSON.stringify(existing))
    return true
  } catch (error) {
    console.error('Failed to update guest pattern timestamp:', error)
    return false
  }
}

/**
 * Get the age of the current guest pattern in milliseconds
 */
export const getGuestPatternAge = (): number | null => {
  const data = loadGuestPattern()
  if (!data) return null
  return Date.now() - data.timestamp
}

/**
 * Check if localStorage quota is available
 */
export const checkStorageQuota = (): { available: boolean; percentUsed: number } => {
  try {
    // Test write
    const testKey = '__storage_test__'
    localStorage.setItem(testKey, 'test')
    localStorage.removeItem(testKey)

    // Estimate usage (rough approximation)
    let totalSize = 0
    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        totalSize += localStorage[key].length + key.length
      }
    }

    // Assuming 5MB limit
    const limitBytes = 5 * 1024 * 1024
    const percentUsed = (totalSize / limitBytes) * 100

    return {
      available: percentUsed < 90,
      percentUsed: Math.min(percentUsed, 100),
    }
  } catch (error) {
    return {
      available: false,
      percentUsed: 100,
    }
  }
}
